import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormField, form } from '@angular/forms/signals';
import { AlertService } from '@/app/atoms/alert/alert.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { CreatePayoutRequestDto, PayoutTypeEnum } from '@/app/common/general-types/payouts.dto';
import { PayoutProviderEnum } from '@/app/common/general-types/wallet-transaction.dto';
import { PayoutsService } from '@/app/services/payouts/payouts.service';
import { PayoutTraceResult } from './payout-trace.types';
import { PayoutTraceChecklist } from './payout-trace-checklist/payout-trace-checklist';

@Component({
  selector: 'admin-tool-payout-retry-fix',
  imports: [PayoutTraceChecklist, FormField],
  templateUrl: './payout-retry-fix.html',
  styleUrl: './payout-retry-fix.scss',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PayoutRetryFix {
  private traceChecklist = viewChild(PayoutTraceChecklist);

  payoutId = signal('');
  actionLoading = signal(false);
  traceResult = signal<PayoutTraceResult | null>(null);

  readonly providerOptions = Object.values(PayoutProviderEnum);

  createPayoutModel = signal({
    blazeFirebaseUid: '',
    briefApplicationId: '',
    currencyCode: 'GBP',
    briefCurrencyCode: 'GBP',
    walletTransactionId: '',
    provider: PayoutProviderEnum.Airwallex,
    amount: 0,
    briefAmount: 0,
  });

  createPayoutForm = form(this.createPayoutModel);

  showRetry = computed(() => !!this.traceResult()?.payoutId);
  showCreate = computed(() => {
    const trace = this.traceResult();
    return !!trace && !trace.payoutId && !!trace.walletTransactionId && !!trace.briefApplicationId;
  });

  private destroyRef = inject(DestroyRef);
  private payoutsService = inject(PayoutsService);
  private alertService = inject(AlertService);
  private toastNotificationService = inject(ToastNotificationService);

  onTraceCompleted(result: PayoutTraceResult | null): void {
    this.traceResult.set(result);
    if (result?.payoutId) {
      this.payoutId.set(result.payoutId);
      return;
    }
    this.payoutId.set('');
    if (result) {
      this.createPayoutModel.set({
        blazeFirebaseUid: result.creatorId,
        briefApplicationId: result.briefApplicationId ?? '',
        currencyCode: result.currencyCode,
        briefCurrencyCode: result.briefCurrencyCode,
        walletTransactionId: result.walletTransactionId ?? '',
        provider: PayoutProviderEnum.Airwallex,
        amount: result.amount,
        briefAmount: result.amount,
      });
    }
  }

  onPayoutIdInput(value: string): void {
    this.payoutId.set(value.trim());
  }

  async runRetry(): Promise<void> {
    const id = this.payoutId().trim();
    if (!id) {
      void this.toastNotificationService.alert({
        title: 'Payout ID required',
        message: 'Enter a payout UUID before retrying.',
        type: 'error',
        icon: 'error',
      });
      return;
    }

    const confirmed = await this.alertService.confirm({
      title: 'Retry payout',
      message: `Retry payout ${id}? This triggers POST /payouts/:id/retry.`,
      confirmText: 'Retry',
      cancelText: 'Cancel',
      type: 'confirm',
      icon: 'warning',
    });

    if (!confirmed) {
      return;
    }

    this.actionLoading.set(true);

    this.payoutsService
      .retryPayout(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.actionLoading.set(false);
          this.traceChecklist()?.refreshPayout();
          void this.toastNotificationService.alert({
            title: 'Payout retried',
            message: 'The payout retry request completed successfully.',
            type: 'success',
            icon: 'success',
          });
        },
        error: () => {
          this.actionLoading.set(false);
          void this.toastNotificationService.alert({
            title: 'Retry failed',
            message: 'Something went wrong while retrying the payout.',
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  async runCreate(): Promise<void> {
    const model = this.createPayoutModel();
    if (
      !model.blazeFirebaseUid.trim() ||
      !model.briefApplicationId.trim() ||
      !model.walletTransactionId.trim()
    ) {
      void this.toastNotificationService.alert({
        title: 'Missing fields',
        message: 'Creator ID, brief application ID, and wallet transaction ID are required.',
        type: 'error',
        icon: 'error',
      });
      return;
    }

    const confirmed = await this.alertService.confirm({
      title: 'Create payout',
      message: `Create payout for application ${model.briefApplicationId} (${model.amount / 100} ${model.currencyCode})?`,
      confirmText: 'Create',
      cancelText: 'Cancel',
      type: 'confirm',
      icon: 'warning',
    });

    if (!confirmed) {
      return;
    }

    const payload: CreatePayoutRequestDto = {
      blazeFirebaseUid: model.blazeFirebaseUid.trim(),
      amount: Number(model.amount),
      briefAmount: Number(model.briefAmount),
      briefCurrencyCode: model.briefCurrencyCode.trim().toUpperCase(),
      conversionRate: 1,
      currencyCode: model.currencyCode.trim().toUpperCase(),
      commission: 0,
      briefApplicationId: model.briefApplicationId.trim(),
      type: PayoutTypeEnum.BriefApplication,
      rankMultiplier: 1,
      provider: model.provider,
      walletTransaction: { id: model.walletTransactionId.trim() },
    };

    this.actionLoading.set(true);

    this.payoutsService
      .createPayout(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (created) => {
          this.actionLoading.set(false);
          if (created?.id) {
            this.payoutId.set(created.id);
            this.traceResult.update((trace) =>
              trace ? { ...trace, payoutId: created.id } : trace,
            );
          }
          this.traceChecklist()?.refreshPayout(created ?? undefined);
          void this.toastNotificationService.alert({
            title: 'Payout created',
            message: created?.id
              ? `Payout ${created.id} was created successfully.`
              : 'Payout was created successfully.',
            type: 'success',
            icon: 'success',
          });
        },
        error: () => {
          this.actionLoading.set(false);
          void this.toastNotificationService.alert({
            title: 'Create failed',
            message: 'Something went wrong while creating the payout.',
            type: 'error',
            icon: 'error',
          });
        },
      });
  }
}
